/**
 * The mission flow (ADR 0086 decision 1, ADR 0094 decision 3): where a mission is in Prepare,
 * Work, Integrate, Verify, Land, Review, Done, who acts next and what, and the steps so far.
 * Derived here from the mission's facts (its launches, sessions, report, landing and close) by
 * the hub on every publish (`mission.flow`) and by the console for demo data, so every surface
 * agrees. ADR 0086's hub-kept state machine (landing reports, follow-up launches) feeds the
 * same shape when it lands. Pure, so every case is tested.
 *
 * The console's five stages (Prepare, Work, Check, Review, Land: `missionStages`, the stepper
 * on the Missions page) are derived here too; the flow maps them onto the runner's seven.
 */
import type { Mission, MissionView } from './missions.ts';
import type { Ts } from '../index.ts';
import { followUpCount, followUpKindOf, integrationNext, orgusLandingDue, orgusLands } from './worktree-integrate.ts';

// ---------------------------------------------------------------------------
// The console's five stages (the stepper)
// ---------------------------------------------------------------------------

export type StageId = 'prepare' | 'work' | 'check' | 'review' | 'land';

/**
 * waiting: not reached (hollow). current: under way now (working tone). attention: under way and
 * waiting on a person (amber). done: finished. failed: stopped with an error. skipped: will not
 * happen (the mission was stopped, or closed without landing).
 */
export type StageState = 'waiting' | 'current' | 'attention' | 'done' | 'failed' | 'skipped';

export interface MissionStage {
  id: StageId;
  label: string;
  state: StageState;
}

export const STAGE_IDS: readonly StageId[] = ['prepare', 'work', 'check', 'review', 'land'];

export const STAGE_LABEL: Record<StageId, string> = { prepare: 'Prepare', work: 'Work', check: 'Check', review: 'Review', land: 'Land' };

export const STAGE_STATE_LABEL: Record<StageState, string> = { waiting: 'Not started', current: 'Under way', attention: 'Needs you', done: 'Done', failed: 'Failed', skipped: 'Skipped' };

/** What a caller may know beyond the mission itself: the landing queue's checks for its branch. */
export interface StageExtra {
  checks?: 'running' | 'passed' | 'failed' | null;
  /** A person accepted the result and sent its branch to land (the run page's Accept and land). */
  reviewed?: boolean;
  /**
   * The project's orgus.yml declares no landing (no flow.land), or it has none: nothing will bring
   * the finished branch to main, so the next step after Work is a person's.
   */
  noLanding?: boolean;
  /**
   * The hub's clock. With it, a finished mission Orgus will not integrate (started before the
   * adoption window, or with no branch recorded) waits on a person, never on Orgus (QA-110).
   */
  now?: Ts;
}

/** The actor id Orgus closes missions as (the hub's SYSTEM actor). */
export const ORGUS_ACTOR_ID = 'orgus';

/** The person's next step when a finished branch has no landing to go to. */
export const UNADOPTED_NEXT = 'Orgus does not land this branch: it finished before Orgus landed missions, or no branch was recorded. Land the branch yourself, or close the mission';
export const NO_LANDING_NEXT = 'This project has no landing configured. Merge the branch into main yourself, or add a landing command (flow.land) to its orgus.yml';

type StageInput = Pick<Mission, 'closed' | 'launches'> & Partial<Pick<Mission, 'landed' | 'report' | 'integration'>>;

export function missionStages(m: StageInput, v: Pick<MissionView, 'state' | 'sessions'>, extra: StageExtra = {}): MissionStage[] {
  const s: Record<StageId, StageState> = { prepare: 'waiting', work: 'waiting', check: 'waiting', review: 'waiting', land: 'waiting' };
  const seen = v.sessions.some((x) => !!x.session);
  const launchFailed = m.launches.length > 0 && m.launches.every((l) => l.result === 'error') && !seen;
  const landed = !!m.landed;
  const stopped = v.state === 'stopped' || m.closed?.as === 'stopped';

  // Prepare: finding a machine and starting the first session.
  if (launchFailed) s.prepare = 'failed';
  // A launch out and no session reported yet: the machine is still starting the agent.
  else if (!seen && !landed && v.state === 'running') s.prepare = 'current';
  else if (seen || landed || v.state === 'running' || v.state === 'waiting' || v.state === 'done' || v.state === 'failed') s.prepare = 'done';
  else if (m.launches.length) s.prepare = 'current';

  // Work: the sessions doing the task.
  if (s.prepare === 'done') {
    if (landed || v.state === 'done') s.work = 'done';
    else if (v.state === 'failed') s.work = 'failed';
    else if (v.state === 'waiting') s.work = 'attention';
    else if (v.state === 'running') s.work = 'current';
  }

  // QA-55: landed by Orgus itself (worktree.integrate), the strip ticks only what ran: Check when
  // its landing ran a command (tests or the project's flow.land), Review when a person reviewed it
  // or Orgus judged the agent's report against the criteria.
  const byOrgus = landed && m.integration?.status === 'landed';
  const ranChecks = !byOrgus || !!m.integration?.tests || m.integration!.steps.some((x) => /\bran /.test(x.why ?? ''));
  const personClosed = m.closed?.as === 'done' && m.closed.by !== ORGUS_ACTOR_ID;
  const reviewed = !byOrgus || !!extra.reviewed || personClosed || !!m.report;

  // Check: the landing queue's checks on its branch.
  if (extra.checks === 'running') s.check = 'current';
  else if (extra.checks === 'failed') s.check = 'failed';
  else if (extra.checks === 'passed' || (landed && ranChecks)) s.check = 'done';
  else if (landed) s.check = 'skipped';

  // Review: a person judges the result against its criteria.
  if (landed && !reviewed) s.review = 'skipped';
  else if (landed || m.closed?.as === 'done' || extra.reviewed) s.review = 'done';
  // Review follows Check: while the checks run it is not reached yet.
  else if (s.work === 'done' && s.check !== 'failed' && s.check !== 'current') s.review = m.report ? 'attention' : 'current';

  // Land: the branch reaches main.
  if (landed) s.land = 'done';
  else if (m.closed?.as === 'done') s.land = 'skipped';

  if (m.closed?.as === 'done' && !landed && s.check === 'waiting') s.check = 'skipped';
  if (stopped) for (const id of STAGE_IDS) if (s[id] === 'waiting' || s[id] === 'current' || s[id] === 'attention') s[id] = 'skipped';

  return STAGE_IDS.map((id) => ({ id, label: STAGE_LABEL[id], state: s[id] }));
}

/** The stage a mission is at: the first one not done (the last one when all are). */
export function currentStage(stages: readonly MissionStage[]): MissionStage {
  return stages.find((x) => x.state === 'failed' || x.state === 'current' || x.state === 'attention') ?? stages.find((x) => x.state === 'waiting') ?? stages[stages.length - 1]!;
}

// ---------------------------------------------------------------------------
// The runner's seven stages: the mission flow
// ---------------------------------------------------------------------------

/** The stages a mission moves through, in order: the node ids of the runtime flow (ADR 0094). */
export const MISSION_FLOW_STAGES = ['prepare', 'work', 'integrate', 'verify', 'land', 'review', 'done'] as const;
export type MissionFlowStage = (typeof MISSION_FLOW_STAGES)[number];

export interface MissionFlowStep {
  at: Ts;
  stage: MissionFlowStage;
  what: string;
  why?: string;
}

export interface MissionFlowNext {
  /** Who acts: Orgus itself, the mission's agent, the landing queue, or a person. */
  who: 'orgus' | 'agent' | 'landing' | 'person';
  what: string;
}

/** `mission.flow`: the stage it is at, each stage's state, who acts next and what, and the steps so far (newest last). */
export interface MissionFlow {
  stage: MissionFlowStage;
  states: Record<MissionFlowStage, StageState>;
  next: MissionFlowNext | null;
  steps: MissionFlowStep[];
}

type FlowMission = Pick<Mission, 'launches' | 'closed'> & Partial<Pick<Mission, 'landed' | 'report' | 'integration' | 'autopilot' | 'paused'>>;

const short = (sha: string) => sha.slice(0, 8);

/** A mission's flow from its facts and its console stages (missionStages). */
export function missionFlowOf(m: FlowMission, v: Pick<MissionView, 'state'>, stages: readonly Pick<MissionStage, 'id' | 'state'>[], extra: Pick<StageExtra, 'noLanding' | 'now'> = {}): MissionFlow {
  const of = (id: StageId): StageState => stages.find((s) => s.id === id)?.state ?? 'waiting';
  const prepare = of('prepare');
  const work = of('work');
  const check = of('check');
  const judge = of('review');
  const land = of('land');
  const landed = !!m.landed;
  const closedDone = m.closed?.as === 'done';
  const stopped = m.closed?.as === 'stopped' || v.state === 'stopped';
  // QA-97: a failed mission is over; Autopilot follows up with a new mission (or holds the item).
  const failed = v.state === 'failed' && !landed;
  // Closed without landing and nothing queued to land: no stage runs any more.
  const closedUnlanded = !!m.closed && !landed && m.integration?.status !== 'submitted';

  const states: Record<MissionFlowStage, StageState> = {
    prepare,
    work,
    // Integrate: Orgus merges main before the checks run; reached once the work is done.
    integrate: landed ? 'done' : check === 'skipped' ? 'skipped' : check !== 'waiting' ? 'done' : work === 'done' ? 'current' : 'waiting',
    // Verify: the checks and the judgement of each criterion (the console's Check and Review before landing).
    verify: landed ? (check === 'skipped' && judge === 'skipped' ? 'skipped' : 'done') : check === 'failed' || judge === 'failed' ? 'failed' : judge === 'attention' ? 'attention' : check === 'current' || judge === 'current' ? 'current' : check === 'done' && judge === 'done' ? 'done' : check === 'skipped' ? 'skipped' : 'waiting',
    land,
    // Review: Orgus (or a person) checks the landed work; reached only once on main.
    review: landed ? (closedDone ? 'done' : 'current') : land === 'skipped' ? 'skipped' : 'waiting',
    done: landed && closedDone ? 'done' : closedDone ? 'skipped' : 'waiting',
  };
  // No landing configured: a branch Orgus does not land itself (a person's terminal mission) waits
  // on a person. A mission Orgus started is landed by Orgus (worktree.integrate's default landing).
  if (extra.noLanding && states.integrate === 'current' && !orgusLands(m)) states.integrate = 'attention';
  // QA-110: a mission Orgus started but will not integrate (too old to adopt, no branch recorded)
  // read 'Orgus: merge main' for days; it is a person's branch to land or drop.
  const unadopted = extra.now !== undefined && states.integrate === 'current' && !m.integration && !m.paused && orgusLands(m) && !orgusLandingDue(m, extra.now);
  if (unadopted) states.integrate = 'attention';
  // Orgus integrating the work (ADR 0086 phase 1): its record says where it is.
  const live = m.integration && !(m.closed && (m.integration.status === 'stopping' || m.integration.status === 'running'));
  const rec = !landed && !stopped && !failed && live ? m.integration! : null;
  // QA-58: a setback an agent fixes is handed to a follow-up agent Orgus starts, a bounded number of times.
  const kind = rec ? followUpKindOf(rec.status) : null;
  const recNext = rec ? integrationNext(rec, kind && orgusLands(m) ? { followUps: followUpCount(m, kind) } : {}) : null;
  // QA-129: a branch in the landing queue ended the work stage, even while its session idles; only
  // an open ask (the view waits on a person) keeps the work stage a person's.
  if (rec?.status === 'submitted' && v.state !== 'waiting' && (states.work === 'current' || states.work === 'attention')) states.work = 'done';
  if (rec && states.work === 'done') {
    if (rec.status === 'submitted') {
      states.integrate = 'done';
      // Verify before landing is phase 2: until it lands, a submitted branch goes straight to Land.
      if (states.verify !== 'done' && states.verify !== 'failed') states.verify = 'skipped';
      states.land = 'current';
    } else if (rec.status === 'stopping' || rec.status === 'running' || (rec.status === 'error' && recNext?.who === 'orgus')) states.integrate = 'current';
    else if (rec.status !== 'landed') states.integrate = 'attention';
  }
  if (stopped) for (const s of MISSION_FLOW_STAGES) if (states[s] === 'waiting' || states[s] === 'current' || states[s] === 'attention') states[s] = 'skipped';
  // Nothing runs after a failure: the stages it never reached are skipped, the failed one stays.
  if (failed || closedUnlanded) for (const s of MISSION_FLOW_STAGES) if (states[s] === 'waiting' || states[s] === 'current') states[s] = 'skipped';

  const stage = MISSION_FLOW_STAGES.find((s) => states[s] === 'failed' || states[s] === 'current' || states[s] === 'attention') ?? MISSION_FLOW_STAGES.find((s) => states[s] === 'waiting') ?? 'done';

  const steps: MissionFlowStep[] = [];
  m.launches.forEach((l, i) => {
    const ok = l.result !== 'error';
    if (l.followUp) {
      // The integration record of the launch it follows says it while that record is current.
      if (ok && m.integration?.launchId === l.followUp.fromLaunchId) return;
      const what = l.followUp.kind === 'conflict' ? 'resolve the merge conflict' : 'fix the failing checks';
      steps.push({ at: l.at, stage: 'integrate', what: ok ? `Started an agent to ${what}` : `The agent to ${what} did not start`, ...(ok ? {} : { why: l.message ?? 'The machine did not answer.' }) });
      return;
    }
    steps.push({ at: l.at, stage: 'prepare', what: i === 0 ? (ok ? `Started a ${l.mode} session on ${l.machineId}` : 'The start failed') : ok ? 'Started a follow-up session on the same branch' : 'A follow-up start failed', ...(ok ? {} : { why: l.message ?? 'The machine did not answer.' }) });
  });
  if (m.report) steps.push({ at: m.report.at, stage: 'work', what: 'Read the final report', why: m.report.acceptance.length ? `${m.report.acceptance.filter((a) => a.met).length} of ${m.report.acceptance.length} criteria reported met` : 'no criteria to report on' });
  for (const x of m.integration?.steps ?? []) steps.push({ at: x.at, stage: 'integrate', what: x.what, ...(x.why ? { why: x.why } : {}) });
  if (m.landed) steps.push({ at: m.landed.at, stage: 'land', what: `Landed ${short(m.landed.sha)} from ${m.landed.branch}`, why: `by ${m.landed.by}` });
  if (m.closed) steps.push({ at: m.closed.at, stage: m.closed.as === 'done' ? 'done' : stage, what: m.closed.as === 'done' ? 'Closed as done' : 'Stopped', why: `by ${m.closed.by}${m.closed.note ? `: ${m.closed.note}` : ''}` });
  steps.sort((a, b) => a.at - b.at);

  let next: MissionFlowNext | null = null;
  if (stopped || (landed && closedDone) || closedUnlanded) next = null;
  else if (failed) next = m.autopilot ? { who: 'orgus', what: 'Retry the item in a new mission, or hold it after repeated failures' } : { who: 'person', what: 'Retry the mission or close it' };
  else if (states.prepare === 'failed') next = { who: 'orgus', what: 'Retry the start on another machine' };
  else if (stage === 'prepare') next = { who: 'orgus', what: 'Start the session' };
  else if (stage === 'work') next = states.work === 'attention' ? { who: 'person', what: 'Answer its ask' } : states.work === 'failed' ? { who: 'orgus', what: 'Class the failure and retry or hold' } : { who: 'agent', what: 'Finish the task and report' };
  else if (rec && (stage === 'integrate' || stage === 'land') && recNext) next = recNext;
  else if (stage === 'integrate') next = unadopted ? { who: 'person', what: UNADOPTED_NEXT } : states.integrate === 'attention' ? { who: 'person', what: NO_LANDING_NEXT } : { who: 'orgus', what: 'Merge main into the branch' };
  else if (stage === 'verify') next = states.verify === 'attention' ? { who: 'person', what: 'Judge the result' } : states.verify === 'failed' ? { who: 'agent', what: 'Fix the red check' } : { who: 'landing', what: 'Run the checks and judge each criterion' };
  else if (stage === 'land') next = { who: 'landing', what: 'Put the branch on main' };
  else if (stage === 'review') next = { who: 'orgus', what: 'Check the landed work against its criteria' };
  else if (stage === 'done') next = { who: 'orgus', what: 'Close the mission' };
  return { stage, states, next, steps };
}

/** The flow of a mission from its facts alone: the console stages derived on the way. */
export function missionFlowFrom(m: FlowMission, v: Pick<MissionView, 'state' | 'sessions'>, extra: StageExtra = {}): MissionFlow {
  return missionFlowOf(m, v, missionStages(m, v, extra), extra);
}
